import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { GoogleIcon, MicrosoftIcon, AppleIcon, SlackIcon } from './AuthIcons';
import { useAuth } from './AuthContext';
export function SignIn() {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const navigate = useNavigate();
  const {
    login
  } = useAuth();
  useEffect(() => {
    // Show info message if redirected from registration
    if (location.state?.email) {
      setInfoMessage('This email is already registered. Please sign in with your password.');
    }
  }, [location.state]);
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    // Basic validation
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!password) {
      setError('Password is required');
      return;
    }
    setIsLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (error: any) {
      console.error('Error signing in:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleSocialSignIn = async (provider: string) => {
    setError('');
    setInfoMessage('');
    setIsLoading(true);
    try {
      const socialEmail = `demo@${provider.toLowerCase()}.com`;
      await login(socialEmail, 'demo');
      navigate('/');
    } catch (error: any) {
      console.error('Error with social sign in:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4" data-id="element-184">
      <div className="max-w-md w-full" data-id="element-185">
        <div className="text-center mb-10" data-id="element-186">
          <h1 className="text-4xl font-bold mb-2" data-id="element-187">Sign In</h1>
          <p className="text-gray-600" data-id="element-188">Welcome back to the MGA Portal</p>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-6" data-id="element-189">
          <div className="mb-8" data-id="element-190">
            <h2 className="text-2xl font-semibold mb-6" data-id="element-191">
              Sign in to your account
            </h2>

            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-700 text-sm" data-id="element-192">
              <strong data-id="element-193">🎭 Demo Mode:</strong> Enter any email and password to
              access the app.
            </div>

            {infoMessage && <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-700 text-sm" data-id="element-194">
                {infoMessage}
              </div>}

            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm" data-id="element-195">
                {error}
              </div>}

            <form onSubmit={handleSignIn} data-id="element-196">
              <div className="mb-4" data-id="element-197">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-198">
                  Email
                </label>
                <input id="email" type="email" value={email} onChange={e => {
                setEmail(e.target.value);
                if (error) setError('');
                if (infoMessage) setInfoMessage('');
              }} placeholder="demo@example.com" className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" required disabled={isLoading} data-id="element-199" />
              </div>

              <div className="mb-6" data-id="element-200">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-201">
                  Password
                </label>
                <input id="password" type="password" value={password} onChange={e => {
                setPassword(e.target.value);
                if (error) setError('');
                if (infoMessage) setInfoMessage('');
              }} placeholder="any password" className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" required disabled={isLoading} data-id="element-202" />
                <div className="mt-2 text-right" data-id="element-203">
                  <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-800 font-medium" data-id="element-204">
                    Forgot password?
                  </Link>
                </div>
              </div>

              <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out flex items-center justify-center" disabled={isLoading} data-id="element-205">
                {isLoading ? <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-206">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-207"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-208"></path>
                    </svg>
                    Signing in...
                  </> : 'Sign in'}
              </button>
            </form>
          </div>

          <div className="relative mb-6" data-id="element-209">
            <div className="absolute inset-0 flex items-center" data-id="element-210">
              <div className="w-full border-t border-gray-300" data-id="element-211"></div>
            </div>
            <div className="relative flex justify-center text-sm" data-id="element-212">
              <span className="px-2 bg-white text-gray-500" data-id="element-213">
                Or continue with
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-214">
            <button onClick={() => handleSocialSignIn('google')} disabled={isLoading} className="flex items-center justify-center py-2.5 px-4 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150" data-id="element-215">
              <GoogleIcon data-id="element-216" />
            </button>
            <button onClick={() => handleSocialSignIn('microsoft')} disabled={isLoading} className="flex items-center justify-center py-2.5 px-4 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150" data-id="element-217">
              <MicrosoftIcon data-id="element-218" />
            </button>
            <button onClick={() => handleSocialSignIn('apple')} disabled={isLoading} className="flex items-center justify-center py-2.5 px-4 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150" data-id="element-219">
              <AppleIcon data-id="element-220" />
            </button>
            <button onClick={() => handleSocialSignIn('slack')} disabled={isLoading} className="flex items-center justify-center py-2.5 px-4 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150" data-id="element-221">
              <SlackIcon data-id="element-222" />
            </button>
          </div>
        </div>

        <div className="text-center" data-id="element-223">
          <p className="text-gray-600" data-id="element-224">
            Don't have an account?{' '}
            <a href="/signup" className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-225">
              Sign up
            </a>
          </p>
        </div>
      </div>
    </div>;
}